"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { BrainView } from "./brainMobileViews";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveTabsLeaderboard from "../my-stream/MyStreamWaveTabsLeaderboard";
import { useLayout } from "../my-stream/layout/LayoutContext";
import { useWave } from "@/hooks/useWave";
import { ArrowLeftIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useUnreadIndicator } from "@/hooks/useUnreadIndicator";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useAuth } from "@/components/auth/Auth";
import { getWaveHomeRoute } from "../../../helpers/navigation.helpers";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import MyStreamWaveCurationCreateDialog from "../my-stream/tabs/MyStreamWaveCurationCreateDialog";
const ACTIVE_TAB_BACKGROUND = "tw-bg-iron-800";
const INACTIVE_TAB_BACKGROUND = "tw-bg-iron-950";
const ACTIVE_TAB_TEXT = "tw-text-iron-300";
const INACTIVE_TAB_TEXT = "tw-text-iron-400";
const BASE_TAB_BUTTON_CLASS_NAME = "tw-border-none tw-no-underline tw-flex tw-justify-center tw-items-center tw-px-2 tw-py-2 tw-gap-1 tw-flex-1 tw-rounded-md";
const BASE_TAB_TEXT_CLASS_NAME = "tw-font-semibold tw-text-xs sm:tw-text-sm tw-whitespace-nowrap";
const getTabButtonClassName = (isActive: boolean): string => `${BASE_TAB_BUTTON_CLASS_NAME} ${isActive ? ACTIVE_TAB_BACKGROUND : INACTIVE_TAB_BACKGROUND}`;
const getTabTextClassName = ({
  isActive,
  additionalClasses
}: {
  readonly isActive: boolean;
  readonly additionalClasses?: string | undefined;
}): string => {
  const additionalClassName = additionalClasses ? ` ${additionalClasses}` : "";
  const textColorClassName = isActive ? ACTIVE_TAB_TEXT : INACTIVE_TAB_TEXT;
  return `${BASE_TAB_TEXT_CLASS_NAME}${additionalClassName} ${textColorClassName}`;
};
interface BrainMobileTabsProps {
  readonly activeView: BrainView;
  readonly onViewChange: (view: BrainView) => void;
  readonly wave?: ApiWave | undefined;
  readonly waveActive: boolean;
  readonly showWavesTab: boolean;
  readonly showStreamBack: boolean;
  readonly isApp?: boolean | undefined;
}
const BrainMobileTabs: React.FC<BrainMobileTabsProps> = t0 => {
  const $ = _c(120);
  const {
    activeView,
    onViewChange,
    wave,
    waveActive,
    showWavesTab,
    showStreamBack,
    isApp
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    registerRef
  } = useLayout();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const [isCreateCurationOpen, setIsCreateCurationOpen] = useState(false);
  const shouldShowCurationTabs = Boolean(isApp && waveActive && wave?.id);
  let t1;
  if ($[0] !== searchParams || $[1] !== shouldShowCurationTabs) {
    t1 = shouldShowCurationTabs ? searchParams.get("curation") : null;
    $[0] = searchParams;
    $[1] = shouldShowCurationTabs;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeCurationId = t1;
  const t2 = wave?.id ?? "";
  let t3;
  if ($[3] !== shouldShowCurationTabs || $[4] !== t2) {
    t3 = {
      waveId: t2,
      enabled: shouldShowCurationTabs
    };
    $[3] = shouldShowCurationTabs;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: t4
  } = useWaveCurations(t3);
  let t5;
  if ($[6] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const curations = t5;
  const canManageCurations = wave?.wave.authenticated_user_eligible_for_admin === true;
  const mobileTabsRef = useRef(null);
  let t6;
  if ($[8] !== registerRef) {
    t6 = element => {
      mobileTabsRef.current = element;
      registerRef("mobileTabs", element);
    };
    $[8] = registerRef;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const setMobileTabsRef = t6;
  const {
    isMemesWave,
    isCurationWave,
    isRankWave
  } = useWave(wave);
  const t7 = connectedProfile?.handle ?? null;
  let t8;
  if ($[10] !== t7) {
    t8 = {
      type: "messages",
      handle: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    hasUnread: hasUnreadMessages
  } = useUnreadIndicator(t8);
  const {
    haveUnreadNotifications
  } = useUnreadNotifications(connectedProfile?.handle ?? null);
  const scrollActiveButtonIntoView = _temp;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = isActive => element_1 => {
      if (isActive) {
        scrollActiveButtonIntoView(element_1);
      }
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const getActiveButtonRef = t9;
  let t10;
  if ($[13] !== pathname || $[14] !== router || $[15] !== searchParams) {
    t10 = curationId => {
      const params = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params.set("curation", curationId);
      } else {
        params.delete("curation");
      }
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[13] = pathname;
    $[14] = router;
    $[15] = searchParams;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const updateSelectedCuration = t10;
  const isChatActive = activeView === BrainView.DEFAULT && activeCurationId === null;
  let t11;
  if ($[17] !== activeCurationId || $[18] !== curations) {
    bb0: {
      const mappedCurations = curations.map(_temp2);
      if (!activeCurationId || mappedCurations.some(curation_0 => curation_0.id === activeCurationId)) {
        t11 = mappedCurations;
        break bb0;
      }
      let t12;
      if ($[20] !== activeCurationId) {
        t12 = {
          id: activeCurationId,
          name: "Curation"
        };
        $[20] = activeCurationId;
        $[21] = t12;
      } else {
        t12 = $[21];
      }
      t11 = [t12, ...mappedCurations];
    }
    $[17] = activeCurationId;
    $[18] = curations;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const curationTabs = t11;
  const showCreateFirstCurationCallout = isApp && canManageCurations && curations.length === 0;
  let t12;
  if ($[22] !== activeCurationId || $[23] !== isApp || $[24] !== onViewChange || $[25] !== updateSelectedCuration) {
    t12 = view => {
      const shouldPreserveSelectedCuration = isApp && view === BrainView.ABOUT && activeCurationId !== null;
      if (!shouldPreserveSelectedCuration) {
        updateSelectedCuration(null);
      }
      onViewChange(view);
    };
    $[22] = activeCurationId;
    $[23] = isApp;
    $[24] = onViewChange;
    $[25] = updateSelectedCuration;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleWaveViewChange = t12;
  let t13;
  if ($[27] !== handleWaveViewChange) {
    t13 = () => {
      handleWaveViewChange(BrainView.DEFAULT);
    };
    $[27] = handleWaveViewChange;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const onChatClick = t13;
  let t14;
  if ($[29] !== handleWaveViewChange) {
    t14 = () => {
      handleWaveViewChange(BrainView.NOTIFICATIONS);
    };
    $[29] = handleWaveViewChange;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const onNotificationsClick = t14;
  let t15;
  if ($[31] !== onViewChange || $[32] !== updateSelectedCuration) {
    t15 = curationId_0 => {
      onViewChange(BrainView.DEFAULT);
      updateSelectedCuration(curationId_0);
    };
    $[31] = onViewChange;
    $[32] = updateSelectedCuration;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const onCurationClick = t15;
  let t16;
  if ($[34] !== activeView || $[35] !== handleWaveViewChange || $[36] !== isCurationWave || $[37] !== wave || $[38] !== waveActive) {
    t16 = waveActive && wave && isCurationWave ? <button ref={getActiveButtonRef(activeView === BrainView.SALES)} onClick={() => handleWaveViewChange(BrainView.SALES)} className={getTabButtonClassName(activeView === BrainView.SALES)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.SALES
      })}>Sales</span></button> : null;
    $[34] = activeView;
    $[35] = handleWaveViewChange;
    $[36] = isCurationWave;
    $[37] = wave;
    $[38] = waveActive;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const salesTabButton = t16;
  let t17;
  if ($[40] !== isApp || $[41] !== onViewChange || $[42] !== router || $[43] !== showStreamBack || $[44] !== waveActive) {
    t17 = waveActive && showStreamBack && !isApp && <><button onClick={() => {
        router.push(getWaveHomeRoute({
          isDirectMessage: false,
          isApp: !!isApp
        }));
        onViewChange(BrainView.DEFAULT);
      }} className="tw-border-none tw-no-underline tw-flex tw-justify-center tw-items-center tw-px-2 tw-py-1.5  tw-gap-1 tw-flex-1 tw-rounded-md tw-bg-iron-950"><ArrowLeftIcon className="tw-size-4 tw-text-iron-400" /><span className="tw-whitespace-nowrap tw-text-xs tw-font-semibold tw-text-iron-400 sm:tw-text-sm">My Stream</span></button><div className="tw-mx-1 tw-h-4 tw-w-px tw-flex-shrink-0 tw-bg-iron-700" /></>;
    $[40] = isApp;
    $[41] = onViewChange;
    $[42] = router;
    $[43] = showStreamBack;
    $[44] = waveActive;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== activeView || $[47] !== handleWaveViewChange || $[48] !== showWavesTab || $[49] !== waveActive) {
    t18 = !waveActive && showWavesTab && <button ref={getActiveButtonRef(activeView === BrainView.WAVES)} onClick={() => handleWaveViewChange(BrainView.WAVES)} className={getTabButtonClassName(activeView === BrainView.WAVES)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.WAVES
      })}>Waves</span></button>;
    $[46] = activeView;
    $[47] = handleWaveViewChange;
    $[48] = showWavesTab;
    $[49] = waveActive;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== activeView || $[52] !== handleWaveViewChange || $[53] !== hasUnreadMessages || $[54] !== isApp || $[55] !== waveActive) {
    t19 = !isApp && !waveActive && <button ref={getActiveButtonRef(activeView === BrainView.MESSAGES)} onClick={() => handleWaveViewChange(BrainView.MESSAGES)} className={getTabButtonClassName(activeView === BrainView.MESSAGES)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.MESSAGES,
        additionalClasses: "tw-relative"
      })}><span>Messages</span>{hasUnreadMessages && <div className="tw-absolute -tw-right-3 tw-top-0 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red" />}</span></button>;
    $[51] = activeView;
    $[52] = handleWaveViewChange;
    $[53] = hasUnreadMessages;
    $[54] = isApp;
    $[55] = waveActive;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== isChatActive) {
    t20 = getActiveButtonRef(isChatActive);
    $[57] = isChatActive;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== isChatActive) {
    t21 = getTabButtonClassName(isChatActive);
    $[59] = isChatActive;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== isChatActive) {
    t22 = getTabTextClassName({
      isActive: isChatActive
    });
    $[61] = isChatActive;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  const t23 = waveActive ? "Chat" : "My Stream";
  let t24;
  if ($[63] !== t22 || $[64] !== t23) {
    t24 = <span className={t22}>{t23}</span>;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== onChatClick || $[67] !== t20 || $[68] !== t21 || $[69] !== t24) {
    t25 = <button ref={t20} onClick={onChatClick} className={t21}>{t24}</button>;
    $[66] = onChatClick;
    $[67] = t20;
    $[68] = t21;
    $[69] = t24;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== activeView || $[72] !== handleWaveViewChange || $[73] !== waveActive) {
    t26 = waveActive && <button ref={getActiveButtonRef(activeView === BrainView.ABOUT)} onClick={() => handleWaveViewChange(BrainView.ABOUT)} className={getTabButtonClassName(activeView === BrainView.ABOUT)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.ABOUT
      })}>About</span></button>;
    $[71] = activeView;
    $[72] = handleWaveViewChange;
    $[73] = waveActive;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  const t27 = !isRankWave && salesTabButton;
  let t28;
  if ($[75] !== activeView || $[76] !== handleWaveViewChange || $[77] !== hasAuthenticatedProfile || $[78] !== isCurationWave || $[79] !== isMemesWave || $[80] !== isRankWave || $[81] !== salesTabButton || $[82] !== wave || $[83] !== waveActive) {
    t28 = waveActive && wave && isRankWave && <><MyStreamWaveTabsLeaderboard wave={wave} activeView={activeView} onViewChange={handleWaveViewChange} renderAfterLeaderboard={salesTabButton} />{(isCurationWave || isMemesWave && hasAuthenticatedProfile) && <><button ref={getActiveButtonRef(activeView === BrainView.MY_VOTES)} onClick={() => handleWaveViewChange(BrainView.MY_VOTES)} className={getTabButtonClassName(activeView === BrainView.MY_VOTES)}><span className={getTabTextClassName({
            isActive: activeView === BrainView.MY_VOTES
          })}>My Votes</span></button></>}<button ref={getActiveButtonRef(activeView === BrainView.OUTCOME)} onClick={() => handleWaveViewChange(BrainView.OUTCOME)} className={getTabButtonClassName(activeView === BrainView.OUTCOME)}><span className={getTabTextClassName({
          isActive: activeView === BrainView.OUTCOME
        })}>Outcome</span></button>{isMemesWave && <button ref={getActiveButtonRef(activeView === BrainView.FAQ)} onClick={() => handleWaveViewChange(BrainView.FAQ)} className={getTabButtonClassName(activeView === BrainView.FAQ)}><span className={getTabTextClassName({
          isActive: activeView === BrainView.FAQ
        })}>FAQ</span></button>}</>;
    $[75] = activeView;
    $[76] = handleWaveViewChange;
    $[77] = hasAuthenticatedProfile;
    $[78] = isCurationWave;
    $[79] = isMemesWave;
    $[80] = isRankWave;
    $[81] = salesTabButton;
    $[82] = wave;
    $[83] = waveActive;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== activeCurationId || $[86] !== activeView || $[87] !== curationTabs || $[88] !== onCurationClick || $[89] !== shouldShowCurationTabs) {
    t29 = shouldShowCurationTabs && curationTabs.map(curation_1 => {
      const isActive_0 = activeView === BrainView.DEFAULT && curation_1.id === activeCurationId;
      return <button key={curation_1.id} type="button" data-curation-id={curation_1.id} ref={getActiveButtonRef(isActive_0)} onClick={() => onCurationClick(curation_1.id)} className={getTabButtonClassName(isActive_0)}><span className={getTabTextClassName({
          isActive: isActive_0,
          additionalClasses: "tw-max-w-28 tw-truncate sm:tw-max-w-36"
        })}>{curation_1.name}</span></button>;
    });
    $[85] = activeCurationId;
    $[86] = activeView;
    $[87] = curationTabs;
    $[88] = onCurationClick;
    $[89] = shouldShowCurationTabs;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== canManageCurations || $[92] !== isApp || $[93] !== showCreateFirstCurationCallout) {
    t30 = isApp && canManageCurations && <button type="button" onClick={() => setIsCreateCurationOpen(true)} className={showCreateFirstCurationCallout ? "tw-inline-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-iron-100 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white" : "tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white"} aria-label="Create curation"><PlusIcon className={`tw-size-4 tw-flex-shrink-0 ${showCreateFirstCurationCallout ? "-tw-ml-1" : ""}`} />{showCreateFirstCurationCallout && <span className="tw-whitespace-nowrap tw-text-xs tw-font-semibold sm:tw-text-sm">Create</span>}</button>;
    $[91] = canManageCurations;
    $[92] = isApp;
    $[93] = showCreateFirstCurationCallout;
    $[94] = t30;
  } else {
    t30 = $[94];
  }
  let t31;
  if ($[95] !== activeView || $[96] !== haveUnreadNotifications || $[97] !== isApp || $[98] !== onNotificationsClick || $[99] !== waveActive) {
    t31 = !isApp && !waveActive && <button ref={getActiveButtonRef(activeView === BrainView.NOTIFICATIONS)} onClick={onNotificationsClick} className={getTabButtonClassName(activeView === BrainView.NOTIFICATIONS)}><span className={getTabTextClassName({
        isActive: activeView === BrainView.NOTIFICATIONS,
        additionalClasses: "tw-relative"
      })}>Notifications{haveUnreadNotifications && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red" />}</span></button>;
    $[95] = activeView;
    $[96] = haveUnreadNotifications;
    $[97] = isApp;
    $[98] = onNotificationsClick;
    $[99] = waveActive;
    $[100] = t31;
  } else {
    t31 = $[100];
  }
  let t32;
  if ($[101] !== t17 || $[102] !== t18 || $[103] !== t19 || $[104] !== t25 || $[105] !== t26 || $[106] !== t27 || $[107] !== t28 || $[108] !== t29 || $[109] !== t30 || $[110] !== t31) {
    t32 = <div className="tw-flex tw-w-full tw-items-center tw-justify-start tw-gap-1 tw-overflow-x-auto tw-overflow-y-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-1 tw-py-1 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t17}{t18}{t19}{t25}{t26}{t27}{t28}{t29}{t30}{t31}</div>;
    $[101] = t17;
    $[102] = t18;
    $[103] = t19;
    $[104] = t25;
    $[105] = t26;
    $[106] = t27;
    $[107] = t28;
    $[108] = t29;
    $[109] = t30;
    $[110] = t31;
    $[111] = t32;
  } else {
    t32 = $[111];
  }
  let t33;
  if ($[112] !== isCreateCurationOpen || $[113] !== onCurationClick || $[114] !== wave) {
    t33 = wave && isCreateCurationOpen && <MyStreamWaveCurationCreateDialog wave={wave} isOpen={isCreateCurationOpen} onClose={() => setIsCreateCurationOpen(false)} onSaved={curation_2 => {
      onCurationClick(curation_2.id);
      setIsCreateCurationOpen(false);
    }} />;
    $[112] = isCreateCurationOpen;
    $[113] = onCurationClick;
    $[114] = wave;
    $[115] = t33;
  } else {
    t33 = $[115];
  }
  let t34;
  if ($[116] !== setMobileTabsRef || $[117] !== t32 || $[118] !== t33) {
    t34 = <div ref={setMobileTabsRef} className="tw-overflow-x-auto tw-px-2 tw-py-2 sm:tw-px-4 md:tw-px-6">{t32}{t33}</div>;
    $[116] = setMobileTabsRef;
    $[117] = t32;
    $[118] = t33;
    $[119] = t34;
  } else {
    t34 = $[119];
  }
  return t34;
};
export default BrainMobileTabs;
function _temp(element_0) {
  element_0?.scrollIntoView({
    behavior: "smooth",
    inline: "center",
    block: "nearest"
  });
}
function _temp2(curation) {
  return {
    id: curation.id,
    name: curation.name
  };
}